<template>
  <div class="mx-0 mb-2 py-2 bg-white shadow">
    <div class="row mx-0" :class="{ 'text-secondary': getAbschnittOptional(textbaustein.id) === 'Inaktiv' }">
      <div class="col-4 fw-bold my-auto">
        <span v-if="getAbschnittOptional(textbaustein.id) === 'Inaktiv'">
          <font-awesome-icon icon="ban" class="me-2" />
        </span>
        {{ textbaustein.name }}
      </div>

      <div v-if="vorlageTextbaustein != undefined" class="col-3 my-auto">{{ textbaustein.titel }}</div>
      <div v-else class="col-4 my-auto">{{ textbaustein.titel }}</div>

      <div class="col-2 my-auto">
        <span v-for="gruppe in gruppen" :key="gruppe" class="badge text-bg-primary me-1 mb-1">{{ gruppe }}</span>
      </div>

      <div v-if="vorlageTextbaustein != undefined" class="col-1 my-auto">{{ getAbschnittOptional(textbaustein.id) }}</div>

      <div class="col-1 my-auto">{{ abschnitteCount }}</div>

      <div class="col-1 my-auto d-flex justify-content-end">
        <div class="btn-group" role="group">
          <button type="button" class="btn btn-primary" @click="toggleCollapse()"><font-awesome-icon icon="magnifying-glass-plus" /></button>

          <router-link v-if="showEdit" :to="{ name: 'EditTextbaustein', query: { textbausteinid: textbaustein.id } }" class="btn btn-secondary">
            <font-awesome-icon icon="pen" />
          </router-link>
        </div>
      </div>
    </div>

    <div class="mt-3 pt-2 border-top border-secondary" :class="{ collapse: isCollapsed }">
      <nav>
        <div class="nav nav-tabs" id="nav-tabs" role="tablist">
          <button
            class="nav-link active"
            :id="'nav-tab-deutsch-' + textbaustein.id"
            data-bs-toggle="tab"
            :data-bs-target="'#nav-deutsch-' + textbaustein.id"
            type="button"
            role="tab"
            :aria-controls="'nav-deutsch-' + textbaustein.id"
            aria-selected="true"
          >
            Deutsch
          </button>

          <button
            class="nav-link"
            :id="'nav-tab-english-' + textbaustein.id"
            data-bs-toggle="tab"
            :data-bs-target="'#nav-english-' + textbaustein.id"
            type="button"
            role="tab"
            :aria-controls="'nav-english-' + textbaustein.id"
            aria-selected="false"
          >
            Englisch
          </button>
        </div>
      </nav>

      <div class="tab-content p-3" id="nav-content">
        <!-- DEUTSCH -->
        <div
          class="tab-pane fade show active"
          :id="'nav-deutsch-' + textbaustein.id"
          role="tabpanel"
          :aria-labelledby="'nav-tab-deutsch-' + textbaustein.id"
          tabindex="0"
        >
          <p v-if="textbaustein.isSignaturBaustein" class="text-center">
            <em>Signatur Paragraph</em>
          </p>

          <p class="text-center">
            <strong v-if="textbaustein.titel != ''">{{ textbaustein.titel }}</strong>
            <span v-if="getAbschnittOptional(textbaustein.id) != null" :class="textbaustein.titel != '' ? 'ms-2' : ''">
              ({{ getAbschnittOptional(textbaustein.id) }})
            </span>
          </p>

          <div class="content-text">
            <div v-html="textbaustein.text"></div>
          </div>

          <div v-for="abschnitt in textbaustein.abschnitte.sort((a, b) => a.position - b.position)" :key="abschnitt.id" class="mt-3">
            <p class="text-center">
              <strong v-if="abschnitt.titel != ''">{{ abschnitt.titel }}</strong>
              <span v-if="getAbschnittOptional(abschnitt.id) != null" :class="abschnitt.titel != '' ? 'ms-2' : ''">
                ({{ getAbschnittOptional(abschnitt.id) }})
              </span>
            </p>

            <div class="content-text">
              <div v-html="abschnitt.text"></div>
            </div>
          </div>
        </div>

        <!-- ENGLISCH -->
        <div class="tab-pane fade" :id="'nav-english-' + textbaustein.id" role="tabpanel" :aria-labelledby="'nav-tab-english-' + textbaustein.id" tabindex="1">
          <p class="text-center">
            <strong v-if="textbaustein.titelEN != ''">{{ textbaustein.titelEN }}</strong>
            <span v-if="getAbschnittOptional(textbaustein.id) != null" :class="textbaustein.titelEN != '' ? 'ms-2' : ''">
              ({{ getAbschnittOptional(textbaustein.id) }})
            </span>
          </p>

          <div class="content-text">
            <div v-html="textbaustein.textEN"></div>
          </div>

          <div v-for="abschnitt in textbaustein.abschnitte.sort((a, b) => a.position - b.position)" :key="abschnitt.id" class="mt-3">
            <p class="text-center">
              <strong v-if="abschnitt.titelEN != ''">{{ abschnitt.titelEN }}</strong>
              <span v-if="getAbschnittOptional(abschnitt.id) != null" :class="abschnitt.titelEN != '' ? 'ms-2' : ''">
                ({{ getAbschnittOptional(abschnitt.id) }})
              </span>
            </p>

            <div class="content-text">
              <div v-html="abschnitt.textEN"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  //#region Imports
  import { PropType, computed, ref } from 'vue';
  import { Textbaustein, VorlageTextbaustein } from '@/models/VorlageModels';
  //#endregion

  const props = defineProps({
    textbaustein: {
      type: Object as PropType<Textbaustein>,
      required: true,
    },

    vorlageTextbaustein: {
      type: Object as PropType<VorlageTextbaustein>,
      required: false,
      default: null,
    },

    showEdit: {
      type: Boolean,
      default: true,
    },
  });

  const isCollapsed = ref(true);
  function toggleCollapse() {
    isCollapsed.value = !isCollapsed.value;
  }

  const abschnitteCount = computed(() => {
    return props.textbaustein.abschnitte.length > 0 ? props.textbaustein.abschnitte.length : '-';
  });

  function getAbschnittOptional(textbausteinId: number): string {
    if (props.vorlageTextbaustein != undefined && props.vorlageTextbaustein.abschnittRegeln != undefined) {
      const regel = props.vorlageTextbaustein.abschnittRegeln.find((ar) => ar.textbausteinId == textbausteinId);
      if (regel != undefined) {
        if (!regel.aktiv) {
          return 'Inaktiv';
        } else {
          return regel.optional ? 'Optional' : 'Standard';
        }
      } else {
        return 'unbekannt';
      }
    } else {
      return 'unbekannt';
    }
  }

  const gruppen = computed(() => {
    const uniqueGruppen = new Array<string>();

    const gruppen = props.textbaustein.gruppe.split(';');

    gruppen.forEach((gruppe) => {
      const trimmedGruppe = gruppe.trim();
      if (trimmedGruppe !== '' && !uniqueGruppen.includes(trimmedGruppe)) {
        uniqueGruppen.push(trimmedGruppe);
      }
    });

    return uniqueGruppen.sort();
  });
</script>
